import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  acceptFriendInvitation,
  acceptUserInvitation,
  checkInvitation,
} from '../../../services/bioplus-services/invitation';
import Logo from '../../../assets/PNG/transparentLogo.png';
import { CenterContent, ContentContainer, Layout, MessageTitle } from './userInvitation.style';
import { IconButton, Label } from '../../../Components/uielements';
import { LoaderWrapper } from '../../../services/settings/appStyle/global.style';
import Spinner from '../../../Components/uielements/loader/spinner';

const AcceptUserInvitation = () => {
  const [searchParams] = useSearchParams();
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const [invitationType, setInvitationType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  const invitationCode = searchParams.get('code');

  useEffect(() => {
    if (!invitationId) {
      setError('Invalid invitation link');
      return;
    }

    const invitationCheck = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await checkInvitation(invitationId);
        setInvitationType(response);

        // Check if user needs to register first
        if (response?.existing === 0) {
          navigate(`/auth/invitations/${invitationId}?code=${invitationCode}`);
        }
      } catch (error) {
        console.error('Error checking invitation:', error);
        setError('Failed to load invitation. Please check the link and try again.');
      } finally {
        setLoading(false);
      }
    };

    invitationCheck();
  }, [invitationId, navigate, invitationCode]); // Added invitationCode to dependencies

  const handleAcceptInvitation = async () => {
    if (!invitationCode) {
      setError('Invalid invitation code');
      return;
    }

    setAccepting(true);
    setError(null);

    try {
      if (invitationType?.role === 'FRIEND') {
        await acceptFriendInvitation(invitationId, { invitationCode });
      } else {
        await acceptUserInvitation(invitationId, { invitationCode });
      }
      navigate('/');
    } catch (error) {
      setError(error || 'Failed to accept invitation. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  // Error state
  if (error && !loading) {
    return (
      <Layout>
        <ContentContainer>
          <CenterContent>
            <img src={Logo} style={{ width: 300 }} alt="Bioplus logo" />
            <MessageTitle style={{ color: '#fff', textAlign: 'center' }}>{error}</MessageTitle>
          </CenterContent>
        </ContentContainer>
      </Layout>
    );
  }

  // Loading state
  if (loading) {
    return (
      <LoaderWrapper>
        <Spinner size="extraMedium" />
        <Label
          style={{ color: '#717171' }}
          variant="italic"
          weight="italic"
          display="block"
          align="center"
          transform="initial"
        >
          Loading...
        </Label>
      </LoaderWrapper>
    );
  }

  // Main content
  return (
    <Layout>
      <ContentContainer>
        <CenterContent>
          <img src={Logo} style={{ width: 300 }} alt="Bioplus logo" />
          <MessageTitle style={{ color: '#fff', textAlign: 'center' }}>
            {invitationType?.role === 'FRIEND'
              ? "To become a friend, please click the 'Accept Invitation' button below."
              : "We're excited to invite you to join our company! Please click the button below to accept and gain access to our application and resources."}
          </MessageTitle>
          <IconButton
            text={accepting ? 'Accepting...' : 'Accept Invitation'}
            onClick={handleAcceptInvitation}
            disabled={accepting}
          />
        </CenterContent>
      </ContentContainer>
    </Layout>
  );
};

export default AcceptUserInvitation;
